import {booleanAttribute, Component, ElementRef, HostBinding, HostListener, Input} from '@angular/core';
import {MatTooltip} from '@angular/material/tooltip';

enum ButtonColors {
    primary = 'primary',
    secondary = 'secondary',
    danger = 'danger'
}

type ButtonContext = {
    active: boolean;
    showActiveChevron: boolean;
}

@Component({
    selector: 'ui-button',
    templateUrl: './ui-button.component.html',
    styleUrls: ['./ui-button.component.scss', 'ui-button.colors.component.scss'],
    standalone: false,
    hostDirectives: [
        {
            directive: MatTooltip,
            inputs: ['matTooltip: tooltip', 'matTooltipPosition: tooltipPosition']
        }
    ]
})
export class UiButtonComponent {
    public context: ButtonContext = {
        active: false,
        showActiveChevron: false,
    };
    @Input() tooltip: string;
    @Input() tooltipPosition: 'left' | 'right' | 'above' | 'below' | 'before' | 'after' = 'below';
    @Input() type: 'button' | 'submit' | 'reset' = 'button';
    @Input() letDoubleClick = false;
    @Input() shape: 'round' | 'square' = 'round';
    @Input({transform: booleanAttribute}) disabled = false;
    @Input() active = false;
    @Input() loading = false;
    @HostBinding('attr.role') hostRole = 'button';

    constructor(public el: ElementRef) {
    }

    private _width: 'fit-content' | 'min-content' | 'max-content' | number | string;

    @Input()
    get width(): string {
        return this._width as string;
    }

    set width(value: 'fit-content' | 'min-content' | 'max-content' | 'full-space' | number | string) {
        switch (value) {
            case 'full-space':
                this._width = '100%';
                break;
            case ('fit-content'):
            case ('max-content'):
            case ('min-content'):
                this._width = value;
                break;
            default:
                this._width = this.convertWidth(value as string);
                break;
        }
    }

    private _variant: 'fill' | 'outline' | 'ghost' | 'link' = 'fill';

    @Input()
    get variant() {
        return this._variant;
    }

    set variant(value: 'fill' | 'outline' | 'ghost' | 'link') {
        this._variant = (typeof value === 'string' ? value.trim().toLowerCase() : 'fill') as 'fill' | 'outline' | 'ghost' | 'link';
    }

    private _color: keyof typeof ButtonColors = ButtonColors.primary;

    @Input()
    get color() {
        return this._color;
    }

    set color(value: keyof typeof ButtonColors) {
        this._color = (typeof value === 'string' ? value.trim().toLowerCase() : 'primary') as keyof typeof ButtonColors;
    }

    get buttonType() {
        const button = this.el.nativeElement as HTMLElement;
        const children = Array.from(button.children);
        const onlyIcons = this.checkIcons(button)

        if (onlyIcons && children.length === 1) {
            return `ui-button__icon ui-button__${this.shape}`;
        } else if (onlyIcons) {
            return 'ui-button__icon multiple-icons ui-button__square';
        } else {
            return `ui-button__text ui-button__${this.shape}`;
        }
    }

    @HostBinding('style.width')
    get hostWidth() {
        return this.width;
    }

    @HostBinding('class') get hostClasses(): string {
        const classes = [
            this.buttonType,
            this.disabled || this.loading ? 'ui-button__disabled' : `ui-button__${this.variant} ${this.color}`,
            this.loading ? 'ui-button__loading' : '',
            this.active || this.context?.active ? 'ui-button__active' : ''
        ];

        return classes.filter(Boolean).join(' ');
    }

    @HostBinding('attr.aria-disabled')
    get ariaDisabled() {
        return this.disabled;
    }

    checkIcons(element: HTMLElement) {
        const children = Array.from(element.children);
        return children.every(child => {
            if (child.tagName.toLowerCase() === 'span') {
                return child.children.length > 0 && this.checkIcons(child as HTMLElement);
            }
            return child.tagName.toLowerCase() === 'ui-icon'
                || child.tagName.toLowerCase() === 'i'
                || child.tagName.toLowerCase() === 'svg'
                || child.classList.contains('show-active-chevron');
        }) && !element.textContent.trim();
    }

    @HostListener('click', ['$event'])
    onClick(event: Event) {
        if (this.disabled || this.loading) {
            event.preventDefault();
            event.stopImmediatePropagation();
        }

        if (this.type === 'submit' || this.type === 'reset') {
            const form = this.el.nativeElement.closest('form');
            if (form) {
                // Cria um botão nativo oculto
                const tmpBtn = document.createElement('button');
                tmpBtn.type = this.type;
                tmpBtn.style.display = 'none';
                form.appendChild(tmpBtn);
                tmpBtn.click();
                form.removeChild(tmpBtn);
            }
        }
    }

    convertWidth(value: string) {
        if (/^\d+$/.test(value)) {
            return value + 'px';
        } else {
            return value;
        }
    }
}
